import type { PlanTier } from '@/types/api';

export type BillingInterval = 'month' | 'year';

/**
 * Static catalogue of pricing tiers. Display copy lives here (rather than
 * being hand-rolled into the Pricing page) so the Account page's "current
 * plan" card can pull the same name/blurb without drift.
 *
 * Pricing is illustrative — the actual amount charged is owned by Stripe
 * (set on each `STRIPE_PRICE_ID_*` env var). If you change the price in
 * the Stripe dashboard, update the matching `priceLabel` here too.
 *
 * Tier ordering matters: `Object.values(PLANS)` iterates in declaration
 * order, which the Pricing page relies on for the left-to-right column
 * layout (Free → Plus → Family).
 */

/** Interval-specific price copy for one tier (monthly or annual). */
export interface PlanPriceCopy {
  /** Human-readable price, e.g. "$5.99/mo" or "$39/yr". Free shows "$0". */
  priceLabel: string;
  /** Optional original price shown struck-through next to the current
   *  price (used for promotional discounts / annual-vs-monthly savings). */
  compareAtLabel?: string;
  /** Optional small note shown under the price (e.g. promo code). */
  discountNote?: string;
  /** Optional savings pill shown next to the price (e.g. "Save 46%"). */
  savingsBadge?: string;
  /** Optional sub-line under the price (e.g. "$3.25/mo, billed annually"). */
  subLabel?: string;
}

export interface PlanCopy {
  tier: PlanTier;
  name: string;
  /** Per-interval price copy. `month` is always present; `year` is only
   *  set for tiers that offer an annual price (Family today). */
  pricing: { month: PlanPriceCopy; year?: PlanPriceCopy };
  /** Short tagline shown under the price. */
  tagline: string;
  /** Marker shown above the card (e.g. "Most popular"). */
  badge?: string;
  /** What the plan includes — rendered as a checklist. */
  features: string[];
  /** Label for the CTA button at the bottom of the card. */
  ctaLabel: string;
}

export const PLANS: Record<PlanTier, PlanCopy> = {
  free: {
    tier: 'free',
    name: 'Ollie Free',
    pricing: { month: { priceLabel: '$0' } },
    tagline: 'Get started — explore the library, no card required.',
    features: [
      'Browse the full catalog',
      'Create up to 1 reader profile',
      'Search Safely™ for Kids',
      'Reading Log & End of Year Review',
    ],
    ctaLabel: 'Current plan',
  },
  plus: {
    tier: 'plus',
    name: 'Ollie Plus',
    pricing: {
      month: { priceLabel: '$7.99/mo', compareAtLabel: '$12.99/mo' },
    },
    tagline: 'For families with curious kids.',
    badge: 'Most popular',
    features: [
      'Everything in Free',
      'Up to 2 readers',
      'Learn with Ollie — Search Safely™ News and Articles for Kids',
      'Book & Article Quizzes',
      'Earn Rewards — Unlock Epic Creatures!',
      'Activity, Vocabulary, and Book Resources!',
      'Ask Ollie — Explore book topics!',
      'Much, much more…',
    ],
    ctaLabel: 'Upgrade to Plus',
  },
  family: {
    tier: 'family',
    name: 'Ollie Family',
    pricing: {
      month: { priceLabel: '$5.99/mo' },
      // Annual option — billed once a year. Savings are framed against the
      // monthly run-rate ($5.99 × 12 = $71.88/yr). Keep `priceLabel`
      // in sync with the Stripe price behind STRIPE_PRICE_ID_FAMILY_ANNUAL.
      year: {
        priceLabel: '$39/yr',
        compareAtLabel: '$71.88/yr',
        savingsBadge: 'Save 46%',
        subLabel: '$3.25/mo, billed annually',
      },
    },
    tagline: 'Everything OllieReads offers, for the whole household.',
    badge: 'Best value',
    // Consolidated paid tier: the Pricing page hides Ollie Plus, so Family
    // carries the full perk list (formerly split between Plus and Family).
    features: [
      'Everything in Free',
      'Up to 5 readers',
      'Learn with Ollie — Search Safely™ News and Articles for Kids',
      'Book & Article Quizzes',
      'Earn Rewards — Unlock Epic Creatures!',
      'Activity, Vocabulary, and Book Resources!',
      'Ask Ollie — Explore book topics!',
      'Much, much more…',
    ],
    ctaLabel: 'Upgrade to Family',
  },
};

/**
 * Resolve the price copy for a tier at a given interval, falling back to the
 * monthly copy when the tier has no annual price (Free, Plus) or when the
 * interval is unknown. Single helper so the Pricing and Account pages never
 * touch `pricing.year` directly (and so the fallback rule lives in one place).
 */
export function priceCopyFor(
  plan: PlanCopy,
  interval: BillingInterval | null | undefined,
): PlanPriceCopy {
  if (interval === 'year' && plan.pricing.year) return plan.pricing.year;
  return plan.pricing.month;
}

/**
 * Canonical tier ordering (free → plus → family). NOTE: the Pricing page
 * currently filters out `plus` — only Free and Family are offered there, with
 * Family carrying the consolidated perk list. `plus` stays in the catalogue so
 * existing Plus subscribers still render correctly on the Account page.
 */
export const PLAN_ORDER: PlanTier[] = ['free', 'plus', 'family'];

/**
 * Numeric rank used by the Pricing page to detect direction of change
 * (upgrade vs. downgrade) when picking CTA copy. Higher = more inclusive
 * tier. Don't switch on this for entitlement decisions — those should
 * read from `account.subscription.isPaid` so the dunning grace window
 * is honoured.
 */
export const TIER_RANK: Record<PlanTier, number> = {
  free: 0,
  plus: 1,
  family: 2,
};

/**
 * Convenience predicate — tier is one of the paid (i.e. Stripe-billed)
 * tiers. Use sparingly; prefer `account.subscription.isPaid` for gating
 * decisions because it also accounts for status (active/trialing/past_due).
 */
export function isPaidTier(tier: PlanTier): tier is 'plus' | 'family' {
  return tier === 'plus' || tier === 'family';
}
